import { createStore, applyMiddleware, combineReducers } from "redux";
import thunk from "redux-thunk";
import storage from "redux-persist/lib/storage";
import { composeWithDevTools } from "redux-devtools-extension";
import * as NotificationStore from "./Common/NotificationStore";
import * as ComposeStore from "../reducers/composeReducer";
import * as CommonStore from "../reducers/commonReducer";
import * as AdminSettingsStore from "../reducers/adminSettingsReducer";
import * as UserAuthStore from "../reducers/authReducer";
import * as UserProfileStore from "../reducers/userProfileReducer";
import * as SecurityQuestionStore from "../reducers/securityQuestionReducer";
import * as ReceivedMessageStore from "../reducers/receivedMessageReducers";
import * as SentMessageStore from "../reducers/sentMessageReducers";
import * as SpamMessageStore from "../reducers/spamMessageReducers";
import * as UserSettingsStore from "../reducers/userSettingsReducer";
import * as FolderStore from "../reducers/folderReducer";
import * as DropOffStore from "../reducers/dropOffReducer";
import * as UnlockStore from "../reducers/unlockReducer";
import * as PostAuthStore from "../reducers/postAuth";
import * as SentItemsHistoryStore from "../reducers/sentItemsHistoryReducer";
import * as ReceivedItemsHistoryStore from "../reducers/receivedItemsHistoryReducer";
import * as DocumentRequestHistoryStore from "../reducers/documentRequestHistoryReducer";
import * as ReportsStore from "../reducers/reportsReducer";
import { persistReducer, persistStore } from "redux-persist";
import { IUserProfile } from "../../src/Core/ViewModels/Profile/ProfileViewModel";
import { IRecipientModel } from "../../src/Core/ViewModels/Message/RecipientModel";
import { recipientQuestionAnswerReducer } from "../../src/reducers/recipientQuestionAnswerReducer";
import { IReceivedMessageModel } from "../../src/Core/ViewModels/Message/ReceivedMessageModel";
import { movingMessageReducer } from "../../src/reducers/movingMessageReducer";
import { IRecipientValidationModel, recipientValidationReducer } from "../../src/reducers/recipientValidationReducers";
import * as SignalRStore from "../reducers/signalRReducer";
import * as rbacStore from "../reducers/rbacReducer";
import * as TemplateStore from "../reducers/templateReducer";
import * as DocumentRequestStore from "../reducers/documentRequestReducer";
import * as RequestDocumentStore from "../reducers/documentRequestReducer";
import * as PreviewDocumentStore from "../reducers/documentRequestReducer";
import { IDropOffRestrictedFilesModel } from "../actionCreators/restrictedFileTypeActionCreators";
import * as RestrictedFileTypeStore from "../reducers/restrictedFileTypeReducer";
import * as DocumentCategoryStore from "../reducers/documentCategoryReducer";
import { IDocumentRequestListModel } from "src/Core/ViewModels/documentRequest/DocumentRequestModel";
import { movingRequestReducer } from "src/reducers/movingRequestReducer";
import * as SavedMessageTemplateStore from "../reducers/SavedMessageReducer";
import * as SavedRequestTemplateStore from "../reducers/savedDRTemplateReducer";

export interface ApplicationState {
    userAuth: UserAuthStore.UserAuthState;
    notification: NotificationStore.NotificationState;
    composeState: ComposeStore.IComposeState;
    commonState: CommonStore.ICommonState;
    userProfile: IUserProfile;
    adminSettingsState: AdminSettingsStore.IAdminSettingsStore;
    securityQuestionState: SecurityQuestionStore.ISecurityQuestionState;
    recipients: IRecipientModel[];
    receivedMessageState: ReceivedMessageStore.IReceivedMessageState;
    sentMessageState: SentMessageStore.ISentMessageState;
    spamMessageState: SpamMessageStore.ISpamMessageState;
    userSettingsState: UserSettingsStore.IUserSettingsStore;
    folderState: FolderStore.IFolderState;
    dropOffState: DropOffStore.IDropOffStore;
    unlockState: UnlockStore.IUnlockStore;
    movingMessages: IReceivedMessageModel[];
    movingRequests: IDocumentRequestListModel[];
    recipientValidationState: IRecipientValidationModel;
    postAuthState: PostAuthStore.IPostAuthState;
    sentItemsReportState: SentItemsHistoryStore.ISentItemsHistoryStore;
    receivedItemsReportState: ReceivedItemsHistoryStore.ISentItemsHistoryStore;
    documentRequestReportState: DocumentRequestHistoryStore.IDocumentRequestHistoryStore;
    reportsState: ReportsStore.IReportsStore;
    signalRState: SignalRStore.ISignalRStore;
    rbacState: rbacStore.IRbacState;
    templateState: TemplateStore.ITemplateState;
    documentRequestState: DocumentRequestStore.IDocumentRequestState;
    documentState: RequestDocumentStore.IDocumentRequestState;
    previewDocumentState: PreviewDocumentStore.IDocumentRequestState;
    restrictedFileTypeState: IDropOffRestrictedFilesModel;
    documentCategoryState: DocumentCategoryStore.IDocumentCategoryStore;
    SavedMessageTemplateState: SavedMessageTemplateStore.ISavedMessageTemplateStore;
    SavedRequestTemplateState: SavedRequestTemplateStore.ISavedRequestMailTemplateStore;
}
export const reducers = {
    userAuth: UserAuthStore.reducer,
    notification: NotificationStore.reducer,
    composeState: ComposeStore.reducer,
    commonState: CommonStore.reducer,
    userProfile: UserProfileStore.reducer,
    adminSettingsState: AdminSettingsStore.reducer,
    securityQuestionState: SecurityQuestionStore.reducer,
    recipients: recipientQuestionAnswerReducer,
    receivedMessageState: ReceivedMessageStore.reducer,
    sentMessageState: SentMessageStore.reducer,
    spamMessageState: SpamMessageStore.reducer,
    userSettingsState: UserSettingsStore.reducer,
    folderState: FolderStore.reducer,
    dropOffState: DropOffStore.reducer,
    unlockState: UnlockStore.reducer,
    movingMessages: movingMessageReducer,
    movingRequests: movingRequestReducer,
    recipientValidationState: recipientValidationReducer,
    postAuthState: PostAuthStore.reducer,
    sentItemsReportState: SentItemsHistoryStore.reducer,
    receivedItemsReportState: ReceivedItemsHistoryStore.reducer,
    documentRequestReportState: DocumentRequestHistoryStore.reducer,
    reportsState: ReportsStore.reducer,
    signalRState: SignalRStore.reducer,
    rbacState: rbacStore.reducer,
    templateState: TemplateStore.reducer,
    documentRequestState: DocumentRequestStore.reducer,
    documentState: RequestDocumentStore.reducer,
    previewDocumentState: PreviewDocumentStore.reducer,
    restrictedFileTypeState: RestrictedFileTypeStore.reducer,
    documentCategoryState: DocumentCategoryStore.reducer,
    SavedMessageTemplateState: SavedMessageTemplateStore.reducer,
    SavedRequestTemplateState: SavedRequestTemplateStore.reducer,
};

export interface AppThunkAction<TAction> {
    (dispatch: (action: TAction | AppThunkAction<TAction>) => void, getState: () => ApplicationState): void;
}

export const persistConfig = {
    key: "root",
    storage: storage,
    blacklist: ["notification", "userAuth", "previewDocumentState", "composeState"],
};

export function configureStore(initialState?: ApplicationState) {
    // Build middleware. These are functions that can process the actions before they reach the store.
    // If devTools is installed, connect to it
    const createStoreWithMiddleware = composeWithDevTools(applyMiddleware(thunk))(createStore);

    // Combine all reducers and instantiate the app-wide store instance
    const reducer = createReducer();
    const persistedReducer = persistReducer(persistConfig, reducer);

    const store = createStoreWithMiddleware(persistedReducer, initialState);
    return store;
}

const createReducer = () => {
    const reducer = combineReducers(reducers);
    return (state, action) => {
        //reset to initial state post logout
        if (action.type === "SIGN_OUT") {
            state = initialState;
        }
        return reducer(state, action);
    };
};

const initialState = (window as any).initialReduxState as ApplicationState;
export const store = configureStore(initialState);
export const persistedStore = persistStore(store);
